import type { PayloadSnapshot, Row, Tier } from './types'
import { compareRows } from './sorting'

export interface Aggregate {
  n: number
  open: number
  overdue: number
  atrisk: number
  stale: number
  stalled: number
  unassigned: number
  blocker: number
  unscored: number
  measured: number
  catfield: number
  nocat: number
  weighted: number
  top: Row | null
  maxidle: number
  todo: number
  prog: number
  limbo: number
  closed: number
  slipped: number
  reslipped: number
  moved: number
  mgrknown: number
  tiers: Record<Tier, number>
}

export function stageOf(row: Row): 'done' | 'todo' | 'prog' {
  return row.cat_key === 'done' ? 'done' : row.cat_key === 'new' ? 'todo' : 'prog'
}

export function isLimbo(row: Row, staleDays: number): boolean {
  return row.open && stageOf(row) === 'todo' && !row.assignee && (row.idle || 0) >= staleDays
}

export function tierCounts(rows: Row[], tiers: Tier[]): Record<Tier, number> {
  const counts = {} as Record<Tier, number>
  tiers.forEach((tier) => { counts[tier] = 0 })
  rows.forEach((row) => { counts[row.tier] = (counts[row.tier] || 0) + 1 })
  return counts
}

export function tierStatus(tiers: Record<Tier, number>): { cls: string, label: string, tier: Tier } {
  if (tiers.critical) return { cls: 'crit', label: 'Critical present', tier: 'critical' }
  if (tiers.high) return { cls: 'warn', label: 'High present', tier: 'high' }
  if (tiers.moderate) return { cls: 'good', label: 'Assessed, none high', tier: 'moderate' }
  return { cls: '', label: 'Nothing assessed', tier: 'unscored' }
}

export function aggregate(rows: Row[], payload: Pick<PayloadSnapshot, 'stale_days' | 'stalled_days' | 'tier_order'>): Aggregate {
  const open = rows.filter((row) => row.open)
  return {
    n: rows.length,
    open: open.length,
    overdue: open.filter((row) => row.due_status === 'overdue').length,
    atrisk: open.filter((row) => row.due_status === 'at-risk').length,
    stale: open.filter((row) => (row.idle || 0) >= payload.stale_days).length,
    stalled: open.filter((row) => (row.idle || 0) >= payload.stalled_days).length,
    unassigned: open.filter((row) => !row.assignee).length,
    blocker: open.filter((row) => row.pri_rank === 0).length,
    unscored: open.filter((row) => row.src === 'missing').length,
    measured: open.filter((row) => row.src === 'measured').length,
    catfield: open.filter((row) => row.cat_src === 'field').length,
    nocat: open.filter((row) => !row.cat).length,
    weighted: open.reduce((sum, row) => sum + (row.score === null ? 0 : row.score), 0),
    top: open.length ? open.slice().sort(compareRows('score'))[0] : null,
    maxidle: open.reduce((max, row) => Math.max(max, row.idle || 0), 0),
    todo: open.filter((row) => stageOf(row) === 'todo').length,
    prog: open.filter((row) => stageOf(row) === 'prog').length,
    limbo: open.filter((row) => isLimbo(row, payload.stale_days)).length,
    closed: rows.filter((row) => !row.open).length,
    slipped: open.filter((row) => row.due_slips > 0).length,
    reslipped: open.filter((row) => row.due_slips > 1).length,
    moved: open.filter((row) => row.due_moves > 0).length,
    mgrknown: open.filter((row) => !!row.mgr).length,
    tiers: tierCounts(open, payload.tier_order),
  }
}
